import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Robot, ChatCircleDots, MagnifyingGlass, ShieldStar } from "@phosphor-icons/react";

export default function DydAccess() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/dyd-access");
      setUsers(r.data.users || []);
    } catch (e) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (u, field, value) => {
    // Optimistic update
    setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, [field]: value } : x));
    try {
      await api.put(`/admin/dyd-access/${u.id}`, { [field]: value });
      toast.success(`${u.name} · ${field === "bot" ? "DYD Bot" : "DYD Coach"} ${value ? "activado" : "desactivado"}`);
    } catch (e) {
      // revert
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, [field]: !value } : x));
      toast.error("Error al actualizar acceso");
    }
  };

  if (user?.role !== "admin") {
    return (
      <Alert variant="destructive" data-testid="dyd-access-no-perm">
        <AlertDescription>Solo el administrador puede gestionar accesos a DYD.</AlertDescription>
      </Alert>
    );
  }

  const filtered = users.filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
  });

  const stats = {
    bot: users.filter((u) => u.bot).length,
    coach: users.filter((u) => u.coach).length,
    total: users.length,
  };

  return (
    <div className="space-y-4" data-testid="dyd-access-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00] flex items-center gap-1">
            <ShieldStar size={12} weight="fill" /> Admin · Control de accesos DYD
          </p>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tight">Quién puede usar DYD Bot y DYD Coach</h1>
          <p className="text-xs text-gray-400 mt-1">Asigna individualmente quién verá el chat flotante (DYD Bot) y la página DYD Coach.</p>
        </div>
        <div className="flex gap-2">
          <div className="card-tactical p-3 min-w-[100px]" data-testid="dyd-access-stat-bot">
            <p className="text-[9px] tracking-[0.2em] uppercase font-bold text-[#CCFF00] flex items-center gap-1"><ChatCircleDots size={10} /> Bot</p>
            <p className="font-['Barlow_Condensed'] text-2xl font-black text-[#CCFF00]">{stats.bot}</p>
            <p className="text-[9px] text-gray-500">de {stats.total}</p>
          </div>
          <div className="card-tactical p-3 min-w-[100px]" data-testid="dyd-access-stat-coach">
            <p className="text-[9px] tracking-[0.2em] uppercase font-bold text-blue-400 flex items-center gap-1"><Robot size={10} /> Coach</p>
            <p className="font-['Barlow_Condensed'] text-2xl font-black text-blue-400">{stats.coach}</p>
            <p className="text-[9px] text-gray-500">de {stats.total}</p>
          </div>
        </div>
      </div>

      <div className="card-tactical p-3">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-2 mb-3">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o email..." className="bg-[#0A0A0A] border-white/15 pl-9" data-testid="dyd-access-search" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/15" data-testid="dyd-access-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/15">
              <SelectItem value="all">Todos los roles</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
              <SelectItem value="directivo">Directivos</SelectItem>
              <SelectItem value="profesor">Profesores</SelectItem>
              <SelectItem value="jugador">Jugadores</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center py-12 text-sm text-gray-500">Cargando usuarios...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-sm text-gray-500">Sin usuarios que coincidan</div>
        ) : (
          <div className="border border-white/5 rounded overflow-hidden">
            <div className="grid grid-cols-[1fr_120px_120px_120px] gap-2 px-3 py-2 bg-white/5 text-[9px] tracking-[0.2em] uppercase font-bold text-gray-400">
              <span>Usuario</span>
              <span className="text-center">Rol</span>
              <span className="text-center">DYD Bot</span>
              <span className="text-center">DYD Coach</span>
            </div>
            <div className="divide-y divide-white/5">
              {filtered.map((u) => {
                const isAdmin = u.role === "admin";
                return (
                  <div key={u.id} className="grid grid-cols-[1fr_120px_120px_120px] gap-2 px-3 py-2 items-center hover:bg-white/[0.02]" data-testid={`dyd-access-row-${u.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white truncate">{u.name || "—"}</p>
                      <p className="text-[10px] text-gray-500 truncate">{u.email}</p>
                    </div>
                    <span className={`text-[10px] uppercase font-bold tracking-wide text-center ${isAdmin ? "text-[#CCFF00]" : u.role === "directivo" ? "text-orange-400" : u.role === "profesor" ? "text-blue-400" : "text-gray-400"}`}>{u.role}</span>
                    <div className="flex justify-center">
                      <Switch
                        checked={isAdmin || u.bot}
                        disabled={isAdmin}
                        onCheckedChange={(v) => toggle(u, "bot", v)}
                        data-testid={`dyd-access-bot-${u.id}`}
                      />
                    </div>
                    <div className="flex justify-center">
                      <Switch
                        checked={isAdmin || u.coach}
                        disabled={isAdmin}
                        onCheckedChange={(v) => toggle(u, "coach", v)}
                        data-testid={`dyd-access-coach-${u.id}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <p className="text-[10px] text-gray-500 italic mt-3">Los administradores tienen acceso total por defecto (switch deshabilitado).</p>
      </div>
    </div>
  );
}
